import { BASE_URL } from "../../../store/services/URL";
import resourcePlannerService from "../resourcePlanner.service";
import { abortableRequest } from "../../../helpers/abortableRequest";
import { readPagination } from "../../../helpers/apiResponse";

const formatWindow = ({ startDate, endDate }) => `${startDate}..${endDate}`;

/**
 * GET resources/planner/page: one page of resources with the structure they work in and their allocations that
 * overlap the windows (closed ranges of ISO dates). Resolves with `{ page, pagination }`.
 */
export function getPlannerPage({ pageNumber = 1, pageSize, projectId, resourceIds, windows = [] }, signal) {
  const query = new URLSearchParams();
  query.append("pageNumber", pageNumber);
  query.append("pageSize", pageSize);
  if (projectId) query.append("projectId", projectId);
  if (resourceIds && resourceIds.length > 0) query.append("resources", resourceIds.join(","));
  windows.forEach((window) => query.append("windows", formatWindow(window)));

  return abortableRequest({ url: `${BASE_URL}resources/planner/page?${query.toString()}`, signal }).then((response) => ({
    page: response.data,
    pagination: readPagination(response),
  }));
}

/**
 * POST resources/planner/allocations: the allocations of some resources that overlap the windows and none of the
 * known ranges, which the client already holds. Windows and ranges are closed ranges of ISO dates.
 */
export function getPlannerAllocations({ resourceIds, windows, knownRanges = [], projectId }, signal) {
  return abortableRequest({
    method: "post",
    url: `${BASE_URL}resources/planner/allocations`,
    data: { resourceIds, windows, knownRanges, projectId: projectId || null },
    signal,
  }).then((response) => response.data);
}

/** Moves an allocation. Resolves with the full response; read the body with `unwrapResult`. */
export const updateAllocation = (taskId, resourceId, body) => resourcePlannerService.updateAllocation(taskId, resourceId, body);

/** Seconds the server asks a client to wait after a 429, or null when the error is something else. */
export function retryAfterSeconds(error) {
  if (error?.response?.status !== 429) return null;

  const seconds = Number(error.response.headers?.["retry-after"]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 10;
}

export const plannerApi = { getPage: getPlannerPage, getAllocations: getPlannerAllocations, updateAllocation };
